import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  companionDirectiveAdd,
  companionDirectiveRenew,
  companionDirectiveStop,
  companionInstructions,
  companionMemory,
  companionMemoryForget,
  companionMemoryPatch,
  companionMemoryPeople,
  companionMemoryTell,
  companionPatterns,
  companionQuiet,
  companionResidents,
  companionRound,
  companionRoundToWorklist,
  companionTrace,
  type CompanionInstructions,
  type CompanionMemory,
  type CompanionMemoryFact,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { A, Sample, firstName, hhmm, shortAge, st, useApi } from './ui';
import type { Lens } from './RichChat';
import { BuildLabel, useCustomerText } from '../../ui/buildLabels';

// Atlas's other sections for Operator, Facility Admin and Care Staff: What it
// knows, Why it did that, Before your round, Standing instructions, Who has gone quiet
// (design T840-1102). Every row is from a route; writes go to the routes and are
// ledgered by the server under the caller's name.

const salienceBar = (v: number, delayMs: number) =>
  `display:block;height:100%;width:${v}%;border-radius:3px;transform-origin:left;animation:growW .55s cubic-bezier(.22,.61,.36,1) forwards;animation-delay:${delayMs}ms;background:${v >= 80 ? '#2F5C97' : v >= 60 ? '#5B6B7C' : 'rgba(34,39,46,.3)'};`;

function ErrLine({ msg }: { msg: string | null }) {
  return msg ? <div role="alert" style={st('font-size:14px;color:#B3261E;line-height:1.45;')}>{msg}</div> : null;
}

/** The resident pills over What it knows and Why it did that (design coPeople). */
function usePeople() {
  const people = useApi((c, signal) => companionMemoryPeople(c, { signal }));
  return people;
}

function People({ list, who, onPick }: { list: { residentId: string; name: string }[]; who: string | null; onPick: (id: string) => void }) {
  return (
    <div style={st('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;min-width:0;')}>
      {list.map((p) => (
        <A
          key={p.residentId}
          selected={who === p.residentId}
          onClick={() => onPick(p.residentId)}
          s={`display:inline-flex;align-items:center;height:30px;padding:0 14px;border-radius:15px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;${who === p.residentId ? 'color:#FCFAF4;background:#2F5C97;' : 'color:#3C454F;background:#FCFAF4;border:1px solid rgba(34,39,46,.18);'}`}
        >
          {p.name}
        </A>
      ))}
    </div>
  );
}

// ── What it knows ─────────────────────────────────────────────────────────────

export function MemorySection({ who, setWho }: { who: string | null; setWho: (id: string) => void }) {
  const plain = useCustomerText();
  const { client } = useSession();
  const people = usePeople();
  const list = people.data?.people ?? [];
  const id = who && list.some((p) => p.residentId === who) ? who : (list[0]?.residentId ?? null);
  const name = list.find((p) => p.residentId === id)?.name ?? '';
  const mem = useApi<CompanionMemory>((c, signal) => companionMemory(c, { params: { id: id! }, signal }), [id], !!id);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const facts = mem.data?.facts ?? [];
  const first = firstName(name);

  const patch = async (f: CompanionMemoryFact, body: { detail?: string; muted?: boolean }) => {
    setErr(null);
    try {
      const r = await companionMemoryPatch(client, { params: { id: id!, factId: f.id }, body });
      mem.setData((p) => (p ? { ...p, facts: p.facts.map((x) => (x.id === f.id ? r.fact : x)) } : p));
    } catch (e) {
      setErr((e as Error).message || 'That change was not saved.');
    }
  };
  const forget = async (f: CompanionMemoryFact) => {
    setErr(null);
    try {
      await companionMemoryForget(client, { params: { id: id!, factId: f.id } });
      mem.setData((p) => (p ? { ...p, facts: p.facts.filter((x) => x.id !== f.id) } : p));
    } catch (e) {
      setErr((e as Error).message || 'That fact was not forgotten.');
    }
  };

  return (
    <div style={st('display:flex;flex-direction:column;gap:22px;min-width:0;overflow:auto;padding:2px 2px 24px;')}>
      <People list={list} who={id} onPick={setWho} />
      {mem.data && (
        <div style={st('min-width:0;')}>
          <div style={st(`font-size:26px;font-weight:700;letter-spacing:-.028em;line-height:1.15;text-wrap:pretty;color:${facts.length ? '#22272E' : '#4A5866'};`)}>
            {`What Atlas knows about ${first}`} <Sample real={mem.data.real} figure="Atlas memory" />
          </div>
          <div style={st('font-size:16px;color:#3C454F;line-height:1.5;margin-top:6px;max-width:70ch;text-wrap:pretty;')}>
            Every fact shows where it came from and how often it comes up. Correct it, stop it being raised, or forget it.
          </div>
        </div>
      )}
      {!mem.data && !mem.loading && (
        <div style={st('font-size:16px;color:#3C454F;line-height:1.5;max-width:70ch;text-wrap:pretty;')}>
          {list.length ? 'The memory for this resident did not load.' : plain('No resident memory to show: the Atlas memory route is not served yet.')}
        </div>
      )}
      {mem.data && facts.length === 0 && (
        <div style={st('padding:14px 16px;border-radius:12px;background:rgba(91,107,124,.08);border:1px solid rgba(91,107,124,.3);font-size:16px;color:#3C454F;line-height:1.5;max-width:80ch;text-wrap:pretty;')}>
          An empty memory means Atlas has nothing to work from. It will not invent a history to fill the gap.
        </div>
      )}
      <div style={st('display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:10px;min-width:0;')}>
        {facts.map((f, i) => {
          const isEditing = editing === f.id;
          return (
            <div key={f.id} style={st(`box-sizing:border-box;min-width:0;padding:9px 0 11px;border-top:1px solid rgba(34,39,46,.14);${f.muted ? 'opacity:.5;' : ''}animation:riseIn .42s cubic-bezier(.22,.61,.36,1);animation-delay:${i * 45}ms;`)}>
              <div style={st('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:4px;min-width:0;')}>
                <span style={st('flex:1 1 120px;min-width:0;font-size:16px;font-weight:700;letter-spacing:-.01em;overflow-wrap:break-word;')}>{f.name}</span>
                <span style={st('display:inline-flex;align-items:center;height:22px;padding:0 8px;border-radius:9px;font-size:14px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;white-space:nowrap;color:#54606C;background:rgba(34,39,46,.07);')}>{f.kind}</span>
              </div>
              {!isEditing && <div style={st('font-size:16px;color:#3C454F;line-height:1.4;margin-top:4px;text-wrap:pretty;')}>{f.detail}</div>}
              {isEditing && (
                <div style={st('margin-top:5px;min-width:0;')}>
                  <input
                    type="text"
                    value={draft}
                    aria-label={`Correct ${f.name}`}
                    onChange={(e) => setDraft(e.target.value)}
                    style={st('box-sizing:border-box;width:100%;height:34px;padding:0 10px;border:1px solid rgba(47,92,151,.45);border-radius:8px;background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;')}
                  />
                  <div style={st('display:flex;align-items:center;gap:8px;margin-top:6px;')}>
                    <A
                      write
                      onClick={() => {
                        const v = draft.trim();
                        setEditing(null);
                        if (v && v !== f.detail) void patch(f, { detail: v });
                      }}
                      s="display:inline-flex;align-items:center;height:28px;padding:0 13px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;color:#FCFAF4;background:#2F5C97;"
                    >
                      Save correction
                    </A>
                    <A onClick={() => setEditing(null)} s="font-size:14px;font-weight:600;color:#54606C;text-decoration:none;">
                      Cancel
                    </A>
                  </div>
                </div>
              )}
              <div style={st('display:flex;align-items:center;gap:8px;margin-top:8px;min-width:0;')}>
                <span style={st('flex:1 1 0;min-width:0;height:5px;border-radius:3px;background:rgba(34,39,46,.09);overflow:hidden;')}>
                  <span style={st(salienceBar(f.salience, i * 45 + 80))} />
                </span>
                <span style={st('flex:0 0 auto;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:#54606C;')}>{String(f.salience)}</span>
                <span style={st('flex:0 0 auto;font-size:14px;color:#54606C;white-space:nowrap;')}>
                  raised <span>{f.lastRaisedAt ? `${shortAge(f.lastRaisedAt)} ago` : 'never'}</span>
                </span>
              </div>
              <div style={st('font-size:14px;color:#54606C;line-height:1.4;margin-top:6px;text-wrap:pretty;')}>
                Knows this because <span>{f.correctedAt ? 'you corrected it · pending the next conversation' : f.provenance}</span>
              </div>
              <div style={st('display:flex;align-items:center;gap:14px;flex-wrap:wrap;margin-top:8px;padding-top:7px;border-top:1px solid rgba(34,39,46,.08);')}>
                <A
                  write
                  onClick={() => {
                    setEditing(f.id);
                    setDraft(f.detail);
                  }}
                  s="flex:0 0 auto;font-size:14px;font-weight:600;color:#2F5C97;text-decoration:none;white-space:nowrap;"
                >
                  Correct
                </A>
                <A write onClick={() => void patch(f, { muted: !f.muted })} s={`flex:0 0 auto;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;color:${f.muted ? '#54606C' : '#4A5866'};`}>
                  {f.muted ? 'Muted' : 'Never raise'}
                </A>
                <A write onClick={() => void forget(f)} s="flex:0 0 auto;font-size:14px;font-weight:600;color:#B3261E;text-decoration:none;white-space:nowrap;">
                  Forget
                </A>
              </div>
            </div>
          );
        })}
      </div>
      <ErrLine msg={err} />
      <div style={st('font-size:14px;color:#54606C;line-height:1.5;max-width:92ch;text-wrap:pretty;')}>
        Correcting, muting or forgetting a fact takes effect on the next conversation and is written to the Truth Ledger under your name. Nothing here can be changed silently.
      </div>
    </div>
  );
}

// ── Why it did that ───────────────────────────────────────────────────────────

const VC: Record<string, string> = { ALLOWED: '#1F6B52', HELD: '#4A5866', BLOCKED: '#B3261E' };

export function WhySection({ lens, who, setWho }: { lens: Lens; who: string | null; setWho: (id: string) => void }) {
  const patterns = useApi((c, signal) => companionPatterns(c, { signal }));
  const people = usePeople();
  const list = people.data?.people ?? [];
  const id = who && list.some((p) => p.residentId === who) ? who : (list[0]?.residentId ?? null);
  const first = firstName(list.find((p) => p.residentId === id)?.name);
  const trace = useApi((c, signal) => companionTrace(c, { params: { id: id! }, signal }), [id], !!id);
  const P = patterns.data?.patterns ?? [];
  const top = P.length ? Math.max(...P.map((p) => p.count)) : 1;
  const decisions = trace.data?.decisions ?? [];
  const facilities = patterns.data?.facilities ?? null;
  return (
    <div style={st('display:flex;flex-direction:column;gap:22px;min-width:0;overflow:auto;padding:2px 2px 24px;')}>
      <div style={st('min-width:0;')}>
        <div style={st('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:3px;padding-bottom:6px;border-bottom:1px solid rgba(34,39,46,.14);')}>
          <span style={st('flex:0 0 auto;font-size:16px;font-weight:600;letter-spacing:-.01em;color:#22272E;')}>What keeps happening</span>
          {patterns.data && <Sample real={patterns.data.real} figure="Refusal patterns" />}
          <span style={st('flex:1 1 200px;min-width:0;font-size:14px;color:#54606C;line-height:1.4;')}>
            {lens === 'admin'
              ? 'this week, in this building · the same reason, not the same resident'
              : `this week, across ${facilities === null ? 'all' : `all ${numberWord(facilities)}`} facilities · the same reason, not the same resident`}
          </span>
        </div>
        <div style={st('display:flex;flex-direction:column;gap:7px;padding-top:9px;min-width:0;')}>
          {!patterns.data && !patterns.loading && <span style={st('font-size:16px;color:#54606C;')}>Refusal patterns are not served yet.</span>}
          {P.map((p, i) => {
            const color = p.severity === 'high' ? '#B3261E' : '#4A5866';
            return (
              <div key={p.reason} style={st('display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:5px;min-width:0;')}>
                <span style={st('flex:0 0 auto;min-width:168px;font-size:16px;font-weight:700;letter-spacing:-.01em;')}>{p.reason}</span>
                <span style={st('flex:0 1 90px;min-width:50px;height:6px;border-radius:3px;background:rgba(34,39,46,.09);overflow:hidden;')}>
                  <span style={st(`display:block;height:100%;width:${Math.round((p.count / top) * 100)}%;border-radius:3px;transform-origin:left;animation:growW .55s cubic-bezier(.22,.61,.36,1) forwards;animation-delay:${i * 60}ms;background:${color};`)} />
                </span>
                <span style={st(`flex:0 0 auto;font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;color:${color};`)}>{String(p.count)}</span>
                <span style={st('flex:0 0 auto;font-size:14px;color:#54606C;white-space:nowrap;')}>{p.where}</span>
                <span style={st('flex:2 1 240px;min-width:0;font-size:14px;color:#3C454F;line-height:1.4;text-wrap:pretty;')}>{p.note}</span>
              </div>
            );
          })}
        </div>
        <div style={st('font-size:14px;color:#54606C;line-height:1.45;padding-top:8px;max-width:98ch;text-wrap:pretty;')}>
          These are refusals that repeat. One is an incident; forty-one is a policy signal. Ranked by how often the reason fired, not by severity.
        </div>
      </div>

      <div style={st('display:flex;align-items:flex-start;gap:10px;flex-wrap:wrap;row-gap:6px;min-width:0;')}>
        <span style={st('display:inline-flex;align-items:center;height:22px;padding:0 8px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.07em;color:#54606C;background:rgba(34,39,46,.09);')}>NOT MEASURED</span>
        <span style={st('flex:1 1 260px;min-width:0;font-size:14px;color:#54606C;line-height:1.5;text-wrap:pretty;')}>
          Whether Atlas is behaving the same way this month as last is the question that matters most, and we do not measure it yet. No persona-drift baseline is running, so this says nothing rather than guessing. Refusal counts above are volume, not behavior.
        </span>
      </div>

      <People list={list} who={id} onPick={setWho} />
      <div style={st('min-width:0;')}>
        <div style={st('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:3px;padding-bottom:5px;border-bottom:1px solid rgba(34,39,46,.14);')}>
          <span style={st('flex:0 0 auto;font-size:16px;font-weight:600;letter-spacing:-.01em;color:#22272E;')}>Why it did what it did</span>
          {trace.data && <Sample real={trace.data.real} figure="Reasoning record" />}
          <span style={st('flex:1 1 200px;min-width:0;font-size:14px;color:#54606C;line-height:1.4;')}>what it did, and the reason it gives · newest first</span>
        </div>
        {trace.data && decisions.length === 0 && (
          <div style={st('font-size:16px;color:#54606C;line-height:1.5;padding-top:9px;max-width:80ch;text-wrap:pretty;')}>
            {`No decisions recorded for ${first}. Nothing has been said to Atlas, so there is nothing to explain.`}
          </div>
        )}
        {decisions.map((t, i) => (
          <div key={`${t.at}-${i}`} style={st(`display:flex;align-items:baseline;gap:10px;padding:7px 0;border-bottom:1px solid rgba(34,39,46,.07);min-width:0;animation:slideIn .4s cubic-bezier(.22,.61,.36,1);animation-delay:${i * 50}ms;`)}>
            <span style={st('flex:0 0 38px;font-size:14px;font-variant-numeric:tabular-nums;color:#54606C;')}>{hhmm(t.at)}</span>
            <span style={st(`flex:0 0 7px;width:7px;height:7px;border-radius:50%;background:${VC[t.verdict]};`)} />
            <span style={st('flex:1 1 0;min-width:0;')}>
              <span style={st('display:block;font-size:16px;font-weight:600;color:#22272E;line-height:1.35;text-wrap:pretty;')}>{t.what}</span>
              <span style={st('display:block;font-size:14px;color:#54606C;line-height:1.35;margin-top:1px;text-wrap:pretty;')}>{t.why}</span>
            </span>
            <span style={st(`flex:0 0 auto;display:inline-flex;align-items:center;height:19px;padding:0 8px;border-radius:5px;font-size:14px;font-weight:700;letter-spacing:.05em;white-space:nowrap;color:${VC[t.verdict]};background:${VC[t.verdict]}1A;`)}>{t.verdict}</span>
          </div>
        ))}
        <div style={st('font-size:14px;color:#54606C;line-height:1.5;padding-top:8px;max-width:92ch;text-wrap:pretty;')}>
          This is the reasoning record, not a transcript. It is what lets you answer “why did it do that” six months later, and it is sealed to the ledger.
        </div>
      </div>
    </div>
  );
}

const WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'];
/** "nine": the design spells small counts in running text. */
export function numberWord(n: number): string {
  return WORDS[n] ?? String(n);
}

// ── Before your round (Care Staff) ────────────────────────────────────────────

const TC: Record<string, string> = { HIGH: '#8C3A33', ELEVATED: '#B3261E', WATCH: '#5B6B7C' };

export function RoundSection() {
  const plain = useCustomerText();
  const { client } = useSession();
  const round = useApi((c, signal) => companionRound(c, { signal }));
  const [done, setDone] = useState<Record<string, boolean>>({});
  const [told, setTold] = useState<Record<string, string>>({});
  const [tellFor, setTellFor] = useState<string | null>(null);
  const [tellDraft, setTellDraft] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const d = round.data;
  const open = (d?.rows ?? []).filter((r) => !done[r.residentId]);
  const shift = d?.shift ?? null;

  const toWorklist = async (rid: string) => {
    setErr(null);
    try {
      await companionRoundToWorklist(client, { params: { residentId: rid } });
      setDone((x) => ({ ...x, [rid]: true }));
    } catch (e) {
      setErr((e as Error).message || 'It was not added to your worklist.');
    }
  };
  const tell = async (rid: string) => {
    const v = tellDraft.trim();
    if (!v) return;
    setErr(null);
    try {
      await companionMemoryTell(client, { params: { id: rid }, body: { text: v } });
      setTold((x) => ({ ...x, [rid]: v }));
      setTellFor(null);
      setTellDraft('');
    } catch (e) {
      setErr((e as Error).message || 'Atlas was not told.');
    }
  };

  return (
    <div data-screen-label="Atlas Today · Before your round" style={st('display:flex;flex-direction:column;min-width:0;')}>
    <div style={st('flex:1 1 auto;display:flex;flex-direction:column;gap:16px;min-width:0;overflow:auto;padding:2px 2px 24px;')}>
      <div style={st('min-width:0;')}>
        <div style={st('display:flex;align-items:baseline;gap:8px;font-size:14px;letter-spacing:.11em;text-transform:uppercase;font-weight:700;color:#54606C;margin-bottom:4px;')}>
          Before your round<span style={st('flex:1 1 auto;')} /><span style={st('text-transform:none;letter-spacing:0;font-weight:500;')}>what changed, in their words</span>
        </div>
        <div style={st(`font-size:16px;font-weight:700;letter-spacing:-.005em;line-height:1.2;text-wrap:pretty;color:${open.length ? '#22272E' : '#12503C'};`)}>
          {!d
            ? round.loading
              ? ''
              : plain('The round list is not served yet')
            : open.length
              ? `${open.length}${open.length === 1 ? ' resident said something' : ' residents said something'} you should know before you walk in`
              : 'Nothing new since your last shift'}
          {d && <> <Sample real={d.real} figure="Before your round" /></>}
        </div>
        <div style={st('font-size:16px;color:#3C454F;line-height:1.5;margin-top:5px;max-width:74ch;text-wrap:pretty;')}>
          Atlas talks to them all day. This is only what changed, in their words, with what it is likely to mean next.
        </div>
      </div>

      {!shift && (
        <div style={st('display:flex;align-items:flex-start;gap:9px;flex-wrap:wrap;padding:9px 12px;border-radius:8px;background:rgba(91,107,124,.08);min-width:0;')}>
          <span style={st('flex:0 0 auto;display:inline-flex;align-items:center;height:22px;padding:0 8px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.07em;color:#4A5866;background:rgba(91,107,124,.16);')}>NO SHIFT DATA</span>
          <span style={st('flex:1 1 240px;min-width:0;font-size:14px;color:#3C454F;line-height:1.5;text-wrap:pretty;')}>
            Scheduling is not connected yet, so we do not know who you are assigned to. This is the whole floor, not your caseload. Once the shift feed is wired this narrows to your residents and the handoff carries over.
          </span>
        </div>
      )}
      {shift && (
        <div style={st('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;min-width:0;')}>
          <span style={st('flex:0 0 auto;font-size:16px;font-weight:700;color:#22272E;')}>{shift.name}</span>
          <span style={st('flex:1 1 200px;min-width:0;font-size:14px;color:#54606C;line-height:1.4;')}>Your caseload for this shift, from the schedule.</span>
        </div>
      )}

      {d && open.length === 0 && (
        <div style={st('font-size:16px;color:#3C454F;line-height:1.5;max-width:74ch;text-wrap:pretty;')}>
          Every resident on this floor has been seen and nothing has moved. That is a real state, not an empty feed.
        </div>
      )}

      <div style={st('min-width:0;')}>
        {open.map((r, i) => {
          const isTelling = tellFor === r.residentId;
          const toldText = told[r.residentId] ?? r.told;
          return (
            <div key={r.residentId} style={st(`display:block;padding:11px 0 13px;border-top:1px solid rgba(34,39,46,.14);min-width:0;animation:riseIn .4s cubic-bezier(.22,.61,.36,1);animation-delay:${i * 55}ms;`)}>
              <div style={st('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:4px;min-width:0;')}>
                <span style={st('flex:0 0 auto;font-size:16px;font-weight:700;letter-spacing:-.015em;white-space:nowrap;')}>{r.name}</span>
                <span style={st('flex:0 0 auto;font-size:14px;color:#54606C;white-space:nowrap;')}>{`Room ${r.room}`}</span>
                <span style={st(`flex:0 0 auto;display:inline-flex;align-items:center;height:18px;padding:0 8px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.07em;white-space:nowrap;color:${TC[r.tier]};background:${TC[r.tier]}1A;`)}>{r.tier}</span>
                <span style={st('flex:1 1 160px;min-width:0;font-size:16px;font-weight:600;color:#22272E;line-height:1.35;text-wrap:pretty;')}>{r.heard}</span>
              </div>
              {r.quote && (
                <div style={st('font-size:16px;font-style:italic;color:#3C454F;line-height:1.45;margin-top:5px;padding-left:11px;border-left:2px solid rgba(47,92,151,.35);text-wrap:pretty;')}>
                  “<span>{r.quote}</span>”
                </div>
              )}
              <div style={st('font-size:14px;color:#3C454F;line-height:1.45;margin-top:5px;max-width:82ch;text-wrap:pretty;')}>{r.meaning}</div>
              <div style={st('display:flex;align-items:flex-start;gap:8px;flex-wrap:wrap;margin-top:5px;min-width:0;')}>
                <span style={st('display:inline-flex;align-items:center;height:22px;padding:0 7px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.07em;color:#5B6B7C;background:rgba(91,107,124,.14);')}>LIKELY NEXT</span>
                <span style={st('flex:1 1 240px;min-width:0;font-size:14px;color:#3C454F;line-height:1.45;text-wrap:pretty;')}>{r.predict}</span>
              </div>
              {r.rule && <div style={st('font-size:14px;font-weight:600;color:#0F6B63;line-height:1.4;margin-top:5px;text-wrap:pretty;')}>{r.rule}</div>}
              {toldText && (
                <div style={st('font-size:14px;color:#54606C;line-height:1.4;margin-top:5px;text-wrap:pretty;')}>
                  You told Atlas: “<span>{toldText}</span>” · in her memory now, with your name on it.
                </div>
              )}
              {!isTelling && (
                <div style={st('display:flex;align-items:center;gap:9px;flex-wrap:wrap;margin-top:8px;')}>
                  <A write onClick={() => void toWorklist(r.residentId)} s="display:inline-flex;align-items:center;height:28px;padding:0 13px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#0F6B63;">
                    Add to my worklist
                  </A>
                  <A
                    write
                    onClick={() => {
                      setTellFor(r.residentId);
                      setTellDraft('');
                    }}
                    s="font-size:14px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;"
                  >
                    Tell Atlas something
                  </A>
                </div>
              )}
              {isTelling && (
                <div style={st('display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:8px;min-width:0;')}>
                  <input
                    type="text"
                    value={tellDraft}
                    onChange={(e) => setTellDraft(e.target.value)}
                    placeholder="What did she tell you that it should know?"
                    aria-label={`What should Atlas know about ${r.name}`}
                    style={st('box-sizing:border-box;flex:1 1 220px;min-width:180px;height:34px;padding:0 12px;border:1px solid rgba(47,92,151,.45);border-radius:8px;background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;')}
                  />
                  <A write onClick={() => void tell(r.residentId)} s="flex:0 0 auto;display:inline-flex;align-items:center;height:34px;padding:0 13px;border-radius:8px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#2F5C97;">
                    Add to memory
                  </A>
                  <A
                    onClick={() => {
                      setTellFor(null);
                      setTellDraft('');
                    }}
                    s="flex:0 0 auto;font-size:14px;font-weight:600;color:#54606C;text-decoration:none;"
                  >
                    Cancel
                  </A>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <ErrLine msg={err} />
      <div style={st('font-size:14px;color:#54606C;line-height:1.5;max-width:96ch;text-wrap:pretty;')}>
        Adding to your worklist puts it in the list you already work from; it does not create a second place to check. Anything you tell Atlas enters that resident’s memory with your name on it, so it can say where it learned it.
      </div>
    </div>
    </div>
  );
}

// ── Standing instructions (Facility Admin) ────────────────────────────────────

type Row = CompanionInstructions['rows'][number];

/** Residents with more than one instruction in force (they may pull against each other). */
export function clashes(rows: Row[]): string[] {
  const by: Record<string, number> = {};
  rows.filter((r) => r.daysLeft > 0).forEach((r) => (by[r.residentName] = (by[r.residentName] ?? 0) + 1));
  return Object.keys(by).filter((k) => by[k] > 1);
}

/** "2h ago", "yesterday", "12d ago": when an instruction last steered a conversation. */
function lastApplied(iso: string | null, now = Date.now()): string {
  if (!iso) return 'never';
  const m = Math.max(0, Math.round((now - Date.parse(iso)) / 60000));
  if (m < 60) return `${m}m ago`;
  if (m < 60 * 24) return `${Math.round(m / 60)}h ago`;
  const days = Math.round(m / 1440);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

export function RulesSection() {
  const plain = useCustomerText();
  const { client } = useSession();
  const inst = useApi((c, signal) => companionInstructions(c, { signal }));
  const residents = useApi((c, signal) => companionResidents(c, { signal }));
  const [forId, setForId] = useState<string>('');
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const opts = residents.data?.residents ?? [];
  useEffect(() => {
    if (!forId && opts[0]) setForId(opts[0].id);
  }, [forId, opts]);
  const rows = inst.data?.rows ?? [];
  const live = rows.filter((r) => r.daysLeft > 0);
  const clash = clashes(rows);

  const add = async () => {
    const v = draft.trim();
    if (!v || !forId) return;
    setErr(null);
    try {
      await companionDirectiveAdd(client, { params: { id: forId }, body: { directive: v } });
      setDraft('');
      inst.reload();
    } catch (e) {
      setErr((e as Error).message || 'The instruction was not set.');
    }
  };
  const stop = async (r: Row) => {
    setErr(null);
    try {
      await companionDirectiveStop(client, { params: { id: r.residentId, did: r.id } });
      inst.setData((p) => (p ? { ...p, rows: p.rows.filter((x) => x.id !== r.id) } : p));
    } catch (e) {
      setErr((e as Error).message || 'The instruction was not stopped.');
    }
  };
  const renew = async (r: Row) => {
    setErr(null);
    try {
      await companionDirectiveRenew(client, { params: { id: r.residentId, did: r.id } });
      inst.setData((p) => (p ? { ...p, rows: p.rows.map((x) => (x.id === r.id ? { ...x, daysLeft: 90 } : x)) } : p));
    } catch (e) {
      setErr((e as Error).message || 'The instruction was not renewed.');
    }
  };

  return (
    <div style={st('flex:1 1 auto;display:flex;flex-direction:column;gap:18px;min-width:0;overflow:auto;padding:2px 2px 24px;')}>
      <div style={st('min-width:0;')}>
        <div style={st('font-size:20px;font-weight:700;letter-spacing:-.024em;line-height:1.2;text-wrap:pretty;color:#22272E;')}>
          {inst.data ? `${live.length} instructions are steering Atlas in this building` : inst.loading ? '' : plain('The building’s instructions are not served yet')}
          {inst.data && <> <Sample real={inst.data.real} figure="Standing instructions" /></>}
        </div>
        <div style={st('font-size:16px;color:#3C454F;line-height:1.5;margin-top:5px;max-width:74ch;text-wrap:pretty;')}>
          These are yours to set. Atlas follows them in every conversation with that resident, and it will still refuse anything clinical no matter what an instruction says.
        </div>
      </div>

      <div style={st('display:flex;align-items:center;gap:8px;flex-wrap:wrap;min-width:0;')}>
        <select
          value={forId}
          aria-label="Resident"
          onChange={(e) => setForId(e.target.value)}
          style={st('box-sizing:border-box;flex:0 0 auto;max-width:200px;height:36px;padding:0 9px;border:1px solid rgba(34,39,46,.16);border-radius:9px;background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;')}
        >
          {opts.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Tell Atlas what to do, or what never to raise"
          aria-label="Instruction"
          style={st('box-sizing:border-box;flex:1 1 240px;min-width:200px;height:36px;padding:0 13px;border:1px solid rgba(34,39,46,.16);border-radius:9px;background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;')}
        />
        {/* Says why it cannot set one yet, instead of a click that does nothing (QA-16). */}
        <A write onClick={() => void add()} disabledReason={!forId ? 'Pick a resident first.' : !draft.trim() ? 'Write the instruction first.' : null} s="flex:0 0 auto;display:inline-flex;align-items:center;height:36px;padding:0 15px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#0F6B63;">
          Set instruction
        </A>
      </div>

      {clash.length > 0 && (
        <div style={st('display:flex;align-items:flex-start;gap:9px;flex-wrap:wrap;padding:9px 12px;border-radius:8px;background:rgba(91,107,124,.08);min-width:0;')}>
          <span style={st('flex:0 0 auto;display:inline-flex;align-items:center;height:22px;padding:0 8px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.07em;color:#4A5866;background:rgba(91,107,124,.16);')}>CHECK THIS</span>
          <span style={st('flex:1 1 240px;min-width:0;font-size:14px;color:#3C454F;line-height:1.5;text-wrap:pretty;')}>
            {`${clash.length}${clash.length === 1 ? ' resident has' : ' residents have'} more than one instruction in force: ${clash.join(', ')}. Check they do not pull against each other, Atlas follows all of them at once.`}
          </span>
        </div>
      )}

      <div style={st('min-width:0;')}>
        {rows.map((r, i) => {
          const dead = r.daysLeft <= 0;
          const soon = !dead && r.daysLeft <= 14;
          return (
            <div key={r.id} style={st(`display:block;padding:9px 0 11px;border-top:1px solid rgba(34,39,46,.14);min-width:0;${dead ? 'opacity:.55;' : ''}animation:riseIn .38s cubic-bezier(.22,.61,.36,1);animation-delay:${i * 45}ms;`)}>
              <div style={st('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:4px;min-width:0;')}>
                <span style={st('flex:1 1 220px;min-width:0;font-size:16px;font-weight:700;letter-spacing:-.01em;line-height:1.35;text-wrap:pretty;')}>{r.text}</span>
                <span style={st('flex:0 0 auto;font-size:14px;font-weight:600;color:#3C454F;white-space:nowrap;')}>{r.residentName}</span>
                {(dead || soon) && (
                  <A write onClick={() => void renew(r)} s="flex:0 0 auto;display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:5px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#0F6B63;">
                    {dead ? 'Renew it' : 'Renew for 90d'}
                  </A>
                )}
                <A write onClick={() => void stop(r)} s="flex:0 0 auto;font-size:14px;font-weight:700;color:#B3261E;text-decoration:none;white-space:nowrap;">
                  Stop it
                </A>
              </div>
              {r.why && <div style={st('font-size:14px;color:#54606C;line-height:1.4;margin-top:2px;text-wrap:pretty;')}>{r.why}</div>}
              <div style={st('display:flex;align-items:center;gap:9px;margin-top:6px;min-width:0;')}>
                <span style={st('flex:0 1 120px;min-width:60px;height:4px;border-radius:2px;background:rgba(34,39,46,.09);overflow:hidden;')}>
                  <span style={st(`display:block;height:100%;width:${Math.round((Math.max(0, r.daysLeft) / 90) * 100)}%;border-radius:2px;transform-origin:left;animation:growW .5s cubic-bezier(.22,.61,.36,1) forwards;animation-delay:${i * 45}ms;background:${dead ? '#B3261E' : r.daysLeft <= 14 ? '#5B6B7C' : '#0F6B63'};`)} />
                </span>
                <span style={st(`flex:0 0 auto;font-size:14px;font-weight:600;color:${dead ? '#B3261E' : r.daysLeft <= 14 ? '#4A5866' : '#54606C'};white-space:nowrap;`)}>{dead ? 'expired' : `expires in ${r.daysLeft}d`}</span>
                <span style={st(`flex:0 0 auto;font-size:14px;font-weight:600;color:${r.timesApplied ? '#1F6B52' : '#4A5866'};white-space:nowrap;`)}>
                  {r.timesApplied ? `steered ${r.timesApplied}${r.timesApplied === 1 ? ' conversation' : ' conversations'} · last ${lastApplied(r.lastAppliedAt)}` : 'never applied yet'}
                </span>
                <span style={st('flex:1 1 0;min-width:0;font-size:14px;color:#54606C;overflow-wrap:break-word;')}>
                  set by <span>{r.by}</span>
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <ErrLine msg={err} />
      <div style={st('font-size:14px;color:#54606C;line-height:1.5;max-width:96ch;text-wrap:pretty;')}>
        Every instruction expires after 90 days and has to be renewed by a person. That cap is enforced by the platform, not by this screen, so an instruction nobody revisits stops steering rather than quietly running forever. Stopping one is written to the Truth Ledger under your name.
      </div>
      <div style={st('display:flex;align-items:flex-start;gap:10px;flex-wrap:wrap;padding-top:11px;border-top:1px solid rgba(34,39,46,.14);min-width:0;')}>
        <span style={st('flex:0 0 auto;display:inline-flex;align-items:center;height:22px;padding:0 8px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.07em;color:#B3261E;background:rgba(179,38,30,.12);')}>HARD FLOOR</span>
        <span style={st('flex:1 1 260px;min-width:0;font-size:14px;color:#3C454F;line-height:1.5;text-wrap:pretty;')}>
          What an instruction can never do: change a dose, give medical advice, withhold a fall from the care team, or stop an emergency escalation. Those floors sit above anything you write here.
        </span>
      </div>
    </div>
  );
}

// ── Who has gone quiet ────────────────────────────────────────────────────────

export function QuietSection() {
  const plain = useCustomerText();
  const quiet = useApi((c, signal) => companionQuiet(c, { signal }));
  const navigate = useNavigate();
  const d = quiet.data;
  const next = d?.likelyNext ?? [];
  return (
    <div style={st('flex:1 1 auto;display:flex;flex-direction:column;gap:22px;min-width:0;overflow:auto;padding:2px 2px 24px;')}>
      <div style={st('min-width:0;')}>
        <div style={st('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:3px;padding-bottom:5px;border-bottom:1px solid rgba(34,39,46,.14);')}>
          <span style={st('flex:0 0 auto;font-size:16px;font-weight:600;letter-spacing:-.01em;color:#22272E;')}>Who has gone quiet</span>
          <span style={st('flex:1 1 200px;min-width:0;font-size:14px;color:#54606C;line-height:1.4;')}>ranked by how long Atlas has been unable to reach them</span>
        </div>
        <div style={st('display:flex;flex-direction:column;gap:3px;padding-top:7px;min-width:0;')}>
          {!d && !quiet.loading && <span style={st('font-size:16px;color:#54606C;')}>Who has gone quiet is not served yet.</span>}
          {(d?.residents ?? []).map((q, i) => {
            const color = q.days >= 20 ? '#B3261E' : q.days >= 8 ? '#4A5866' : '#54606C';
            return (
              <A
                key={q.residentId}
                onClick={() => navigate(`/home?member=${encodeURIComponent(q.residentId)}`)}
                s="display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:4px;padding:5px 8px;border-radius:5px;min-width:0;text-decoration:none;color:inherit;"
                hover="background:rgba(47,92,151,.06);"
              >
                <span style={st('flex:0 0 auto;min-width:120px;font-size:16px;font-weight:600;white-space:nowrap;')}>{q.name}</span>
                <span style={st('flex:0 0 auto;font-size:14px;color:#54606C;white-space:nowrap;')}>{q.place}</span>
                <span style={st('flex:1 1 90px;min-width:0;height:5px;border-radius:3px;background:rgba(34,39,46,.09);overflow:hidden;')}>
                  <span style={st(`display:block;height:100%;width:${Math.min(100, q.days * 3.5)}%;border-radius:3px;transform-origin:left;animation:growW .55s cubic-bezier(.22,.61,.36,1) forwards;animation-delay:${i * 60}ms;background:${q.days >= 20 ? '#B3261E' : q.days >= 8 ? '#5B6B7C' : '#54606C'};`)} />
                </span>
                <span style={st(`flex:0 0 auto;font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;color:${color};`)}>{`${q.days}d`}</span>
                <span style={st('flex:1 1 160px;min-width:0;font-size:14px;color:#54606C;line-height:1.35;text-wrap:pretty;')}>{q.note}</span>
              </A>
            );
          })}
        </div>
      </div>

      {d && next.length > 0 && (
        <div style={st('display:flex;align-items:flex-start;gap:10px;flex-wrap:wrap;row-gap:6px;padding:12px 14px;border-radius:12px;background:rgba(91,107,124,.08);border:1px solid rgba(91,107,124,.26);min-width:0;')}>
          <BuildLabel><span style={st('display:inline-flex;align-items:center;height:22px;padding:0 8px;border-radius:5px;font-size:14px;font-weight:700;letter-spacing:.07em;color:#5B6B7C;background:rgba(91,107,124,.14);')}>MODELED</span></BuildLabel>
          <span style={st('flex:1 1 260px;min-width:0;font-size:16px;color:#3C454F;line-height:1.5;text-wrap:pretty;')}>
            {`Conversation frequency is one of the eight detector inputs. On the current pattern, ${joinNames(next)} ${next.length === 1 ? 'is the most likely' : `are the ${numberWord(next.length)} most likely`} to show a health dip next; quiet weeks lead deterioration more reliably than any single reading.`}
          </span>
        </div>
      )}

      <div style={st('font-size:14px;color:#54606C;line-height:1.5;max-width:96ch;text-wrap:pretty;')}>
        {d && d.real
          ? 'Indicators read each resident against their own baseline and never act on their own.'
          : plain('SAMPLE. The memory graph, reasoning trace and directive endpoints exist per resident; there is no estate-wide rollup yet, so this view is illustrative. Indicators read each resident against their own baseline and never act on their own.')}
      </div>
    </div>
  );
}

/** "Elmer Vance and Doris Lang", "A, B and C". */
export function joinNames(n: string[]): string {
  return n.length <= 1 ? (n[0] ?? '') : `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
}
